import { useEffect, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Aviso } from '@/components/ui/aviso';
import { PasosDelAsistente } from '@/components/ui/pasos-del-asistente';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import {
  FrenoDeLaDemo,
  frenoDelError,
  type FrenoDeLaDemoProps,
} from '@/features/mini-prixia/freno-de-la-demo';
import { pricingRulesApi } from '../services/pricing-rules-api';
import type {
  BorradorInicial,
  ConteosDeReglas,
  CreateRulePayload,
  PricingRule,
} from '../types/pricing-rules.types';
import {
  ALCANCE_VACIO,
  LIMITE_VACIO,
  accionesDe,
  alcanceDesde,
  alcanceListo,
  avisoAlEditar,
  cuerpoDeGuardado,
  fraseDeAlcance,
  limiteDesde,
  limiteDesdeInicial,
  nombreAutomatico,
  nombreDeRegla,
  nombreInicial,
  scopeDe,
  type EstadoAlcance,
  type EstadoLimite,
} from '../lib/nueva-regla';
import { describirReglaCompleta } from '../lib/reglas-en-palabras';
import { FilaDelResumen, ResumenDelAsistente } from '@/features/compite/ui/resumen-del-asistente';
import { PasoLimite } from './paso-limite';
import { PasoAQuien } from './paso-a-quien';
import { PasoQueCambiaria } from './paso-que-cambiaria';
import { BOTON_DEGRADADO } from '@/features/compite/ui/boton-degradado';
import { atributosSoloMira, useSoporteSoloMira } from '@/features/mini-prixia/soporte-solo-mira';

export interface NuevaReglaDialogProps {
  abierto: boolean;
  /** La regla a editar; `null` = una nueva. */
  regla: PricingRule | null;
  /** Lo que trae «¿Y si…?» (S10) para empezar ya lleno. */
  inicial?: BorradorInicial | null;
  reglas: PricingRule[];
  conteos: ConteosDeReglas | null;
  /**
   * Permiso de encender/apagar. Crear encendida lo pide (Task 0, ajuste 5):
   * sin él, una nueva sólo se guarda apagada. Editar no lo usa.
   */
  puedeEncender: boolean;
  onCerrar: () => void;
  onGuardada: (r: PricingRule) => void;
}

/** El número de cada paso lo pone `PasosDelAsistente` (pulido, spec 232). */
const PASOS = ['Qué límite', 'A quién', 'Qué cambiaría'];

/** Nueva regla en tres pasos (maqueta S2, S3, S4). */
export function NuevaReglaDialog({
  abierto,
  regla,
  inicial = null,
  reglas,
  conteos,
  puedeEncender,
  onCerrar,
  onGuardada,
}: NuevaReglaDialogProps) {
  const { toast } = useToast();
  // Soporte («Entrar como») en una demo sólo mira: no guarda reglas.
  const sinCambios = atributosSoloMira(useSoporteSoloMira());
  const [paso, setPaso] = useState(0);
  const [limite, setLimite] = useState<EstadoLimite>(LIMITE_VACIO);
  const [alcance, setAlcance] = useState<EstadoAlcance>(ALCANCE_VACIO);
  // Vacío = el nombre automático (Sebastián, 27-sep: se le puede poner nombre).
  const [nombre, setNombre] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [errorAlGuardar, setErrorAlGuardar] = useState<string | null>(null);
  // Reglas de la demo (343): la regla alcanza productos propios; se dice en el cuadro, con «Ver el plan completo».
  const [freno, setFreno] = useState<FrenoDeLaDemoProps | null>(null);

  // Al abrir: precargar lo de la regla, o lo de «¿Y si…?», o vacío.
  useEffect(() => {
    if (!abierto) return;
    setPaso(0);
    setFreno(null);
    setErrorAlGuardar(null);
    setNombre(nombreInicial(regla));
    if (regla) {
      setLimite(limiteDesde(regla.actions));
      setAlcance(alcanceDesde(regla.scope));
    } else if (inicial) {
      setLimite(limiteDesdeInicial(inicial));
      setAlcance(alcanceDesde(inicial.scope));
    } else {
      setLimite(LIMITE_VACIO);
      setAlcance(ALCANCE_VACIO);
    }
  }, [abierto, regla, inicial]);

  const acciones = accionesDe(limite);
  const scope = scopeDe(alcance);
  const aviso = avisoAlEditar(regla, { acciones, alcance });
  const puedeSeguir =
    paso === 0 ? acciones != null : paso === 1 ? alcanceListo(alcance, limite.tipo) : true;

  async function guardar(encender: boolean) {
    if (!acciones) return;
    setFreno(null);
    setGuardando(true);
    setErrorAlGuardar(null);
    try {
      const cuerpo = cuerpoDeGuardado({
        actions: acciones,
        scope,
        editando: regla,
        encender,
        nombre,
      });
      const r = regla
        ? await pricingRulesApi.update(regla.id, cuerpo)
        : await pricingRulesApi.create(cuerpo as CreateRulePayload);
      toast({
        // Spec 294 (D-R4-4): nada rige hasta aplicar.
        title: regla ? 'Cambio guardado sin aplicar' : 'Regla guardada sin aplicar',
        description: 'Rige cuando apliques las reglas y recalcules.',
      });
      onGuardada(r);
    } catch (e) {
      const delFreno = frenoDelError(e);
      if (delFreno) {
        setFreno(delFreno);
        return;
      }
      setErrorAlGuardar(
        mensajeDeError(e, 'No se pudo guardar la regla. Revisá los valores y probá de nuevo.')
      );
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && onCerrar()}>
      {/* Grande (Sebastián, 27-sep): casi toda la pantalla; el medio se desplaza y los
          botones quedan siempre a la vista, aunque crezcan las excepciones.
          Rediseño de Compite (15-oct): encabezado con la píldora del módulo, el paso a
          la izquierda y «Tu regla» a la derecha, y los botones en una barra fija. */}
      <DialogContent className="flex h-[92vh] w-[96vw] max-w-[1600px] flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="space-y-0 border-b px-6 pb-4 pt-5 text-left sm:px-8">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            <SlidersHorizontal aria-hidden="true" className="h-3.5 w-3.5" />
            Compite · Reglas
          </span>
          <DialogTitle className="tracking-tight pt-3 text-2xl font-semibold">
            {paso === 0
              ? '¿Qué límite querés poner?'
              : paso === 1
                ? '¿A quién alcanza?'
                : '¿Qué cambiaría si la encendés?'}
          </DialogTitle>
          <DialogDescription className="pt-1">
            {regla ? 'Editar regla' : 'Nueva regla'}
            {/* El mismo nombre que se guarda (cuerpoDeGuardado) y que anuncia el aviso. */}
            {acciones ? ` · ${nombreDeRegla(nombre, acciones, scope)}` : ''}
          </DialogDescription>
          <PasosDelAsistente pasos={PASOS} actual={paso} className="pt-4" />
        </DialogHeader>

        <div className="flex min-h-0 flex-1">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5 sm:px-8">
            {aviso && (
              <Aviso tono="alerta" className="text-sm">
                {aviso}
              </Aviso>
            )}

            {paso === 0 && <PasoLimite estado={limite} onChange={setLimite} conteos={conteos} />}
            {paso === 1 && acciones && (
              <PasoAQuien
                alcance={alcance}
                onChange={setAlcance}
                acciones={acciones}
                editandoId={regla?.id ?? null}
                reglas={reglas}
                conteos={conteos}
              />
            )}
            {paso === 2 && acciones && (
              <>
                <div className="max-w-xl">
                  <label htmlFor="nombre-regla" className="mb-1 block text-sm font-medium">
                    Nombre (opcional)
                  </label>
                  <Input
                    id="nombre-regla"
                    maxLength={200}
                    placeholder={nombreAutomatico(acciones, scope)}
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Vacío, se usa el de ejemplo. En la lista se ve siempre el límite al lado del
                    nombre.
                  </p>
                </div>
                <PasoQueCambiaria
                  acciones={acciones}
                  scope={scope}
                  editandoId={regla?.id ?? null}
                />
              </>
            )}

            {freno && <FrenoDeLaDemo {...freno} />}
            {errorAlGuardar && (
              <p role="alert" className="text-sm text-destructive">
                {errorAlGuardar}
              </p>
            )}
          </div>

          {/* Lo que se va armando, a la vista en los tres pasos (en pantallas anchas). */}
          <ResumenDelAsistente titulo="Tu regla">
            <FilaDelResumen
              titulo="El límite"
              valor={acciones ? describirReglaCompleta(acciones) : null}
              vacio="Elegí un límite."
            />
            <FilaDelResumen
              titulo="A quién"
              valor={acciones && paso > 0 ? fraseDeAlcance(alcance, limite.tipo) : null}
              vacio="Lo elegís en el paso 2."
            />
            <FilaDelResumen
              titulo="Cuándo rige"
              valor="Cuando apliques las reglas y recalcules. Hasta entonces, nada cambia."
            />
          </ResumenDelAsistente>
        </div>

        <DialogFooter className="items-center gap-2 border-t bg-muted/30 px-6 py-3 sm:px-8">
          <span className="text-xs text-muted-foreground sm:mr-auto">
            Paso {paso + 1} de {PASOS.length}
          </span>
          {/* Ronda final (B10): «Cancelar» fijo (ghost) y «Atrás» desde el paso 2,
              como en «Crear una estrategia». */}
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          {paso > 0 && (
            <Button variant="outline" onClick={() => setPaso(paso - 1)}>
              Atrás
            </Button>
          )}
          {paso < 2 ? (
            <Button
              disabled={!puedeSeguir}
              onClick={() => setPaso(paso + 1)}
              className={BOTON_DEGRADADO}
            >
              {paso === 0 ? 'Siguiente: a quién' : 'Siguiente: qué cambiaría'}
            </Button>
          ) : regla ? (
            <Button
              disabled={guardando}
              onClick={() => void guardar(false)}
              className={BOTON_DEGRADADO}
              {...sinCambios}
            >
              Guardar cambios
            </Button>
          ) : puedeEncender ? (
            <>
              <Button
                variant="outline"
                disabled={guardando}
                onClick={() => void guardar(false)}
                {...sinCambios}
              >
                Guardar apagada
              </Button>
              <Button
                disabled={guardando}
                onClick={() => void guardar(true)}
                className={BOTON_DEGRADADO}
                {...sinCambios}
              >
                Encender la regla
              </Button>
            </>
          ) : (
            // Sin permiso de encender: se crea apagada, con `isActive: false`
            // explícito (Task 0, ajuste 5; sin la llave el backend la enciende).
            <>
              <span className="mr-auto self-center text-xs text-muted-foreground">
                Queda apagada: la enciende quien tenga permiso para encender reglas.
              </span>
              <Button disabled={guardando} onClick={() => void guardar(false)} {...sinCambios}>
                Guardar apagada
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
